import React, { useContext, useState, useRef, useEffect } from "react";
import intl from "react-intl-universal";
import { mdiClose, mdiCalculator } from "@mdi/js";
import commonUtils from "../../../script/utils/commonUtils";
import styled from "styled-components";

import calculationRest from "../../../rest/calculationRest";
import worstcaseDataRest from "../../../rest/worstcaseDataRest";
import calcSettingUtils from "../../../script/utils/calcSettingUtils";

import { useInput } from "../../hooks/useInput";
import { useOnChange } from "../../hooks/useOnChange";
import { EntryContext } from "../../contexts/EntryContext";
import { ValidationContext } from "../../contexts/ValidationContext";

import { Grid } from "../../components/Grid";
import { Dialog } from "../../containers/Dialog";
import { Button } from "../../components/Button";
import { Spacer } from "../../components/Spacer";
import { Checkbox } from "../../components/Checkbox";
import { DialogBody } from "../../containers/DialogBody";
import { useValidation } from "../../hooks/useValidation";
import { InputNumber } from "../../components/InputNumber";
import { DialogFooter } from "../../containers/DialogFooter";
import { SelectSingle } from "../../components/SelectSingle";
import { SelectMultiple } from "../../components/SelectMultiple";

const DialogBodyStyle = styled.div`
    min-height: 300px;
`;

export const WorstcaseDialog = props => {

    const entryContext = useContext(EntryContext);
    const validationContext = useContext(ValidationContext);
    const { entry } = entryContext;

    const getCalcSetting = () => {
        if(entry.calcSetting === undefined || entry.calcSetting === null) {
            return calcSettingUtils.empty();
        }
        return calcSettingUtils.clone(entry.calcSetting);
    };

    const [calcSetting, setCalcSetting] = useState(getCalcSetting());
    const [isFarfieldDataAvailable, setFarfieldDataAvailable] = useState(false);
    const [isNearfieldDataAvailable, setNearfieldDataAvailable] = useState(false);
    const [isWorstcaseAllowed, setWorstcaseAllowed] = useState(calcSetting.resolution <= 625);
    const worstcaseFarField = useInput("worstcaseFarField", true);

    const calcAreasRef = useRef([]);
    const calcAreas = useInput("calcAreas", calcAreasRef.current);
    const resolution = useInput("resolution", calcSetting.resolution);
    const environmentFactor = useInput("environmentFactor", calcSetting.environmentFactor);
    const sliceNum = useInput("sliceNum", calcSetting.sliceNum);
    const sliceDist = useInput("sliceDist", calcSetting.sliceDist);

    useValidation(value => !commonUtils.isBlank(value), [environmentFactor]);

    useOnChange((name, value) => {
        calcSetting[name] = value;
        setCalcSetting(calcSetting);
        if (name === "resolution") {
            setWorstcaseAllowed(calcSetting.resolution <= 625);
        }
    }, [environmentFactor, resolution, sliceNum, sliceDist]);

    useEffect(() => {
        (async () => {
            const isFarfieldAvailable = await worstcaseDataRest.isAvailable(entry.id, true);
            const isNearfieldAvailable = await worstcaseDataRest.isAvailable(entry.id, false);
            setNearfieldDataAvailable(isNearfieldAvailable);
            setFarfieldDataAvailable(isFarfieldAvailable);
        })();
    }, []);

    const onWorstcase = async () => {
        const allValid = await validationContext.allValid();
        validationContext.setErrorsVisible(true);
        const { entry } = entryContext;
        entry.calcSetting = calcSetting;
        if (allValid) {
            await props.saveEntry(entry);
            const calcAreaIds = calcAreas.value.map(area => area.id);
            const updatedEntry = await calculationRest.worstcase(entry.id, calcAreaIds, worstcaseFarField.value);
            entryContext.setEntry(updatedEntry);
            props.onClose();
        }
    };

    return (
        <Dialog
            width="430px"
            title={intl.get("entry.worstcase")}
            onClose={() => props.onClose()}>
            <DialogBody>
                <DialogBodyStyle>
                    <Grid columns="1">
                        <SelectMultiple {...calcAreas.bind}
                            title={intl.get("entry.worstcaseSetting.areas")}
                            disabled={entry.calcSetting.calcAreas.length <= 0}
                            toLabel={value => value.name}>
                            {entry.calcSetting.calcAreas
                                .filter(ca => ca.name !== "OVERVIEW")
                                .map(ca => (
                                    <option key={ca.id} value={ca}>{ca.name}</option>
                                ))
                            }
                        </SelectMultiple>
                        <SelectSingle {...resolution.bind}
                            title={intl.get("entry.worstcaseSetting.resolution")}>
                            <option value="625">625</option>
                            <option value="500">500</option>
                            <option value="375">375</option>
                            <option value="250">250</option>
                            <option value="125">125</option>
                            <option value="0">0</option>
                        </SelectSingle>
                        <InputNumber {...environmentFactor.bind}
                            fractionDigits={4}
                            title={intl.get("entry.worstcaseSetting.environmentFactor")} />
                        <Checkbox {...worstcaseFarField.bind} title={intl.get("entry.wattwaechterSetting.preferFarField")} disabled={!isNearfieldDataAvailable} />
                        <Spacer size="small" variant="vertical" />
                    </Grid>
                    <Grid columns="2">
                        <InputNumber {...sliceNum.bind} title={intl.get("entry.wattwaechterSetting.sliceNum")} />
                        <InputNumber {...sliceDist.bind} title={intl.get("entry.wattwaechterSetting.sliceDist")} fractionDigits={2} />
                    </Grid>
                </DialogBodyStyle>
            </DialogBody>
            <DialogFooter>
                <Button
                    svgIcon={mdiClose}
                    onClick={() => props.onClose()}
                    value={intl.get("common.cancel")} />
                <Spacer variant="vertical" />
                <Button
                    disabled={calcAreas.value.length <= 0 || !isFarfieldDataAvailable || !isWorstcaseAllowed}
                    svgIcon={mdiCalculator}
                    onClick={onWorstcase}
                    value={intl.get("entry.calculate")} />
            </DialogFooter>
        </Dialog>
    );
};